import React, { useState, useEffect } from "react";
import { FolderOpen } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useUser } from "../../context/UserContext";
import { createSession, generateAdminFileDownloadUrl, generateSessionFilePresignedUploadUrl, generateUserFileDownloadUrl, getMyDetails } from "../../service/apiService";
import FileManagementModal from "./FileManagementModal";
import { useModel } from "../../context/ModelContext"; 
import { useSession } from "../../context/SessionContext"; 
import { toast } from "sonner"; 
import useFileUpload from "../../hooks/useFileUpload";
import { useFileTransfer } from "../../context/FileTransferContext";

interface FileManagementProps {
  className?: string;
  onClose?: () => void;
}

// Add this interface if it's not already accessible
interface FileRow {
  file_id: string;
  file_name: string;
  original_file_name?: string;
  file_format: string;
  uploaded_at: string;
  uploaded_by_name: string;
  storage_location: 'session' | 'brainbank' | 'direct';
  session_id?: string;
  status?: string;
  knowledge_base_name?: string;
  department?: string | null;
}

const FileManagement: React.FC<FileManagementProps> = ({ className, onClose }) => {
  const { isAuthenticated } = useAuth();
  const { userId, userDetails } = useUser(); // Get userDetails from context instead of fetching
  const [isModalOpen, setIsModalOpen] = useState(true); // Auto-open since component only renders when needed
  const [isFileLoading, setIsFileLoading] = useState<string | null>(null);
  const { selectedModel } = useModel();
  const { sessionId, setSessionId } = useSession();
  const { handleFilesSelected } = useFileUpload();
  const { transferFile } = useFileTransfer();
  const [fileContent, setFileContent] = useState<File | null>(null);

  // Derive isAdmin from userDetails (no need to fetch again)
  const isAdmin = userDetails?.role === 'admin';

  // Helper function for MIME type
  function getFileTypeFromName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const mimeTypes: {[key: string]: string} = {
      'pdf': 'application/pdf',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'txt': 'text/plain',
      'csv': 'text/csv',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };
    
    return mimeTypes[extension] || 'application/octet-stream';
  }

  // Handle file selection 
  const handleFileSelect = async (file: FileRow) => {
    try {
      setIsFileLoading(file.file_id);
      
      // Get the file download URL
      const apiFileType = file.storage_location === 'direct' ? 'brainbank' : file.storage_location;
      const downloadResponse = isAdmin 
        ? await generateAdminFileDownloadUrl(file.file_id, apiFileType)
        : await generateUserFileDownloadUrl(file.file_id, apiFileType);
      
      // Download the file
      const fileResponse = await fetch(downloadResponse.presigned_url);
      if (!fileResponse.ok) {
        throw new Error(`Failed to download file: ${fileResponse.status}`);
      }
      
      const blob = await fileResponse.blob();
      
      // Create a File object under the user's ORIGINAL name — the chat upload
      // flow re-mints its own stored name from it, so passing the already-minted
      // storage name here would double-suffix the file.
      const displayName = file.original_file_name || file.file_name;
      const newFile = new File(
        [blob],
        displayName,
        { type: getFileTypeFromName(file.file_name) }
      );

      // Close the modal
      setIsModalOpen(false);

      // Transfer the file using our context
      transferFile(newFile);

      toast.success(`File "${displayName}" added to message input`);
      
    } catch (error) {
      console.error("Error handling file selection:", error);
      toast.error("Failed to add file to message input");
    } finally {
      setIsFileLoading(null);
    }
  };

  // Handle chat with file
  const handleChatWithFile = async (file: FileRow) => {
    try {
      setIsFileLoading(file.file_id);
      console.log("🚀 Starting chat with file:", file.file_name);
      
      // Step 1: Download file
      console.log("📥 Downloading file...");
      const apiFileType = file.storage_location === 'direct' ? 'brainbank' : file.storage_location;
      const downloadResponse = isAdmin 
        ? await generateAdminFileDownloadUrl(file.file_id, apiFileType)
        : await generateUserFileDownloadUrl(file.file_id, apiFileType);
      
      console.log("📁 Fetching file content...");
      const fileResponse = await fetch(downloadResponse.presigned_url);
      if (!fileResponse.ok) {
        throw new Error(`Failed to download file: ${fileResponse.status}`);
      }
      
      const blob = await fileResponse.blob();
      // Original name for the same double-mint reason as handleFileSelect above.
      const newFile = new File([blob], file.original_file_name || file.file_name, {
        type: getFileTypeFromName(file.file_name)
      });
      
      console.log("✅ File ready:", newFile.name);
      
      // Step 2: Close modal and transfer file
      setIsModalOpen(false);
      transferFile(newFile);
      
      // Step 3: Show appropriate success message
      if (sessionId) {
        toast.success(`File "${file.file_name}" will be added to current conversation`);
      } else {
        toast.success(`Starting new chat with "${file.file_name}"`);
      }
      
      console.log("✅ Chat with file completed successfully");
      
    } catch (error) {
      console.error("❌ Error in chat with file:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to start chat with file: ${errorMessage}`);
    } finally {
      setIsFileLoading(null);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <FileManagementModal 
      isOpen={isModalOpen}
      onClose={handleClose}
      isAdmin={isAdmin}
      userDetails={userDetails}
      onFileSelect={handleFileSelect}       
      onChatWithFile={handleChatWithFile}   
      isFileLoading={isFileLoading}
    />
  );
};

export default FileManagement;